<template>
    <div>
        <dynamic-toolbar
            title="Customer KPIs"
            icon="mdi-chart-timeline-variant-shimmer"
            color="teal darken-2"
            theme="dark"
            :loading="loading.collections"
        ></dynamic-toolbar>

        <v-card elevation="2">
            <v-card-title>
                <v-row class="mt-1">
                    <v-spacer></v-spacer>
                    <v-col cols="12" md="2">
                        <v-autocomplete
                            v-model="params.customer"
                            :items="collections.customers"
                            label="Customer"
                            hint="Select the customer"
                            item-text="name"
                            item-value="id"
                            outlined
                            return-object
                            dense
                            persistent-hint
                            prepend-inner-icon="mdi-account-multiple"
                            @change="handleChangeParams"
                        ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-menu
                            v-model="dateMenu.startDate"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="params.startDate"
                                    label="Start Date"
                                    prepend-inner-icon="mdi-calendar"
                                    single-line
                                    placeholder="YYYY-MM-DD"
                                    v-mask="'####-##-##'"
                                    outlined
                                    hint="Select the start date"
                                    persistent-hint
                                    dense
                                    v-bind="attrs"
                                    v-on="on"
                                    @change="handleChangeParams"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="params.startDate"
                                no-title
                                @input="dateMenu.startDate = false"
                            ></v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-menu
                            v-model="dateMenu.endDate"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="params.endDate"
                                    label="End Date"
                                    prepend-inner-icon="mdi-calendar"
                                    single-line
                                    placeholder="YYYY-MM-DD"
                                    v-mask="'####-##-##'"
                                    outlined
                                    hint="Select the end date"
                                    persistent-hint
                                    dense
                                    v-bind="attrs"
                                    v-on="on"
                                    @change="handleChangeParams"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="params.endDate"
                                no-title
                                @input="dateMenu.endDate = false"
                            ></v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-btn
                            color="success"
                            depressed
                            @click="getReport"
                            style="margin-top: .5px;"
                        >
                            <v-icon left>mdi-chart-tree</v-icon>
                            Get Report
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text v-if="loading.kpis">
                <v-row>
                    <v-col
                        class="text-h4 mt-1"
                        cols="10"
                        style="color: #004D40;"
                    >
                        <v-icon color="teal darken-4">mdi-account-multiple</v-icon>
                        {{ params.customer?.Name }}
                    </v-col>
                    <v-spacer></v-spacer>
                    <v-col cols="2">
                        <v-btn
                            color="success"
                            depressed
                            @click="downloadReport"
                            class="mt-5"
                        >
                            <v-icon left>mdi-file-excel</v-icon>
                            Download Report
                        </v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-data-table
                            :headers="kpi_headers"
                            :items="kpis.kpis_by_region_with_region_name"
                            show-expand
                            single-expand
                            disable-sort
                            :expanded.sync="expanded"
                            item-key="region_name"
                        >
                            <template slot="body.append">
                                <tr>
                                    <td colspan="2"></td>
                                    <td class="text-left font-weight-bold">
                                        <strong class="text-left font-weight-bold">
                                            {{ getFormattedNumberField(kpis.kpis_totals.total_kms) }}
                                        </strong>
                                    </td>
                                    <td class="text-left font-weight-bold">
                                        <strong class="text-left font-weight-bold">
                                            {{ getFormattedNumberField(kpis.kpis_totals.total_hrs) }}
                                        </strong>
                                    </td>
                                    <td class="text-left font-weight-bold">
                                        <strong class="text-left font-weight-bold">
                                            {{ getFormattedNumberField(kpis.kpis_totals.total_loads, 0) }}
                                        </strong>
                                    </td>
                                    <td class="text-left font-weight-bold">
                                        <strong class="text-left font-weight-bold">
                                            {{ getFormattedNumberField(kpis.kpis_totals.total_drivers, 0) }}
                                        </strong>
                                    </td>
                                    <td class="text-left font-weight-bold">
                                        <strong class="text-left font-weight-bold">
                                            {{ getFormattedNumberField(kpis.kpis_totals.total_trucks, 0) }}
                                        </strong>
                                    </td>
                                    <td class="text-left font-weight-bold">
                                        <strong class="text-left font-weight-bold">
                                            {{ getFormattedNumberField(kpis.kpis_totals.fte_drivers) }}
                                        </strong>
                                    </td>
                                    <td class="text-left font-weight-bold">
                                        <strong class="text-left font-weight-bold">
                                            {{ getFormattedNumberField(kpis.kpis_totals.fte_trucks) }}
                                        </strong>
                                    </td>
                                    <td class="text-left font-weight-bold">
                                        <strong class="text-left font-weight-bold">
                                            {{ getFormattedNumberField(kpis.kpis_totals.total_liters) }}
                                        </strong>
                                    </td>
                                </tr>
                            </template>

                            <template v-slot:item.total_kms="{ item }">
                                {{ getFormattedNumberField(item.total_kms) }}
                            </template>

                            <template v-slot:item.total_hrs="{ item }">
                                {{ getFormattedNumberField(item.total_hrs) }}
                            </template>

                            <template v-slot:item.total_loads="{ item }">
                                {{ getFormattedNumberField(item.total_loads, 0) }}
                            </template>

                            <template v-slot:item.total_drivers="{ item }">
                                {{ getFormattedNumberField(item.total_drivers, 0) }}
                            </template>

                            <template v-slot:item.total_trucks="{ item }">
                                {{ getFormattedNumberField(item.total_trucks, 0) }}
                            </template>

                            <template v-slot:item.fte_drivers="{ item }">
                                {{ getFormattedNumberField(item.fte_drivers) }}
                            </template>

                            <template v-slot:item.fte_trucks="{ item }">
                                {{ getFormattedNumberField(item.fte_trucks) }}
                            </template>

                            <template v-slot:item.total_liters="{ item }">
                                {{ getFormattedNumberField(item.total_liters) }}
                            </template>

                            <template v-slot:expanded-item="{ headers, item }">
                                <td :colspan="headers.length" style="background-color: #fff;padding: 0;">
                                    <v-card flat>
                                        <v-card-text>
                                            <v-row>
                                                <v-col
                                                    v-for="(terminals, month) in kpis.kpis_by_region_by_month_by_terminal[item.region_name]"
                                                    :key="month"
                                                    cols="6"
                                                >
                                                    <v-card outlined>
                                                        <v-card-title class="text-h6">{{ month }}</v-card-title>
                                                        <v-card-subtitle>KPIs Summary</v-card-subtitle>
                                                        <table style="width: 100%; border-collapse: collapse;">
                                                            <thead>
                                                                <tr>
                                                                    <th style="padding: 8px; border: 1px solid #ddd;text-align: left;color: #004D40!important;">Terminal</th>
                                                                    <th style="padding: 8px; border: 1px solid #ddd;text-align: left;color: #004D40!important;">KM</th>
                                                                    <th style="padding: 8px; border: 1px solid #ddd;text-align: left;color: #004D40!important;">Hours</th>
                                                                    <th style="padding: 8px; border: 1px solid #ddd;text-align: left;color: #004D40!important;">Loads</th>
                                                                    <th style="padding: 8px; border: 1px solid #ddd;text-align: left;color: #004D40!important;">Drivers</th>
                                                                    <th style="padding: 8px; border: 1px solid #ddd;text-align: left;color: #004D40!important;">Trucks</th>
                                                                    <th style="padding: 8px; border: 1px solid #ddd;text-align: left;color: #004D40!important;">FTE Drivers</th>
                                                                    <th style="padding: 8px; border: 1px solid #ddd;text-align: left;color: #004D40!important;">FTE Trucks</th>
                                                                    <th style="padding: 8px; border: 1px solid #ddd;text-align: left;color: #004D40!important;">Liters</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr v-for="(kpis, terminal) in terminals" :key="terminal">
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">{{ terminal }}</td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">{{ getFormattedNumberField(kpis.total_kms) }}</td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">{{ getFormattedNumberField(kpis.total_hrs) }}</td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">{{ getFormattedNumberField(kpis.total_loads, 0) }}</td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">{{ getFormattedNumberField(kpis.total_drivers, 0) }}</td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">{{ getFormattedNumberField(kpis.total_trucks, 0) }}</td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">{{ getFormattedNumberField(kpis.fte_drivers) }}</td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">{{ getFormattedNumberField(kpis.fte_trucks) }}</td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">{{ getFormattedNumberField(kpis.total_liters) }}</td>
                                                                </tr>
                                                            </tbody>
                                                            <tfoot>
                                                                <tr style="background-color: #e0e0e0;">
                                                                    <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">Totals</td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">
                                                                        <strong>
                                                                            {{ getFormattedNumberField(kpis.kpis_by_region_by_month[item.region_name][month].total_kms) }}
                                                                        </strong>
                                                                    </td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">
                                                                        <strong>
                                                                            {{ getFormattedNumberField(kpis.kpis_by_region_by_month[item.region_name][month].total_hrs) }}
                                                                        </strong>
                                                                    </td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">
                                                                        <strong>
                                                                            {{ getFormattedNumberField(kpis.kpis_by_region_by_month[item.region_name][month].total_loads, 0) }}
                                                                        </strong>
                                                                    </td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">
                                                                        <strong>
                                                                            {{ getFormattedNumberField(kpis.kpis_by_region_by_month[item.region_name][month].total_drivers, 0) }}
                                                                        </strong>
                                                                    </td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">
                                                                        <strong>
                                                                            {{ getFormattedNumberField(kpis.kpis_by_region_by_month[item.region_name][month].total_trucks, 0) }}
                                                                        </strong>
                                                                    </td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">
                                                                        <strong>
                                                                            {{ getFormattedNumberField(kpis.kpis_by_region_by_month[item.region_name][month].fte_drivers) }}
                                                                        </strong>
                                                                    </td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">
                                                                        <strong>
                                                                            {{ getFormattedNumberField(kpis.kpis_by_region_by_month[item.region_name][month].fte_trucks) }}
                                                                        </strong>
                                                                    </td>
                                                                    <td style="padding: 8px; border: 1px solid #ddd;">
                                                                        <strong>
                                                                            {{ getFormattedNumberField(kpis.kpis_by_region_by_month[item.region_name][month].total_liters) }}
                                                                        </strong>
                                                                    </td>
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </v-card>
                                                </v-col>
                                            </v-row>
                                        </v-card-text>
                                    </v-card>
                                </td>
                            </template>
                        </v-data-table>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import DynamicToolbar from '../../../components/Global/Utils/DynamicToolbar.vue';
import { mask } from 'vue-the-mask';
import VueContext from 'vue-context';

import axios from 'axios';
import { validations } from '@/Utils/validation';

export default {
    name: 'CustomerKPIsMain',
    directives: { mask },
    components: {
        VueContext,
        DynamicToolbar,
    },
    data() {
        return {
            rules: validations,
            loading: {
                collections: true,
                kpis: false,
            },
            collections: {},
            dateMenu: {
                startDate: false,
                endDate: false,
            },
            params: {
                startDate: '',
                endDate: '',
                customer: null,
            },
            expanded: [],
            kpi_headers: [
                { text: '', value: 'data-table-expand', class: 'custom-hd' },
                { text: 'Region', value: 'region_name', class: 'custom-hd' },
                { text: 'KM', value: 'total_kms', class: 'custom-hd' },
                { text: 'Hours', value: 'total_hrs', class: 'custom-hd' },
                { text: 'Loads', value: 'total_loads', class: 'custom-hd' },
                { text: 'Drivers', value: 'total_drivers', class: 'custom-hd' },
                { text: 'Trucks', value: 'total_trucks', class: 'custom-hd' },
                { text: 'FTE Drivers', value: 'fte_drivers', class: 'custom-hd' },
                { text: 'FTE Trucks', value: 'fte_trucks', class: 'custom-hd' },
                { text: 'Liters', value: 'total_liters', class: 'custom-hd' },
            ],
            kpis: {}
        };
    },
    computed: {
        
    },
    mounted() {
        this.getCollections();
    },
    methods: {
        getCollections() {
            console.log('Get collections');
            this.loading.collections = true;
            axios
                .get('v2/kpis/collections')
                .then(response => {
                    this.collections = response.data;

                    console.log('collections ', this.collections);
                    this.loading.collections = false;
                })
                .catch(error => {
                    console.error(error);
                });
        },
        downloadReport() {
            console.log('Download report');
            let str = this.$store;
            str.state.isLoading = true;

            let filename = 'KPI-REPORT-' + this.params.customer?.Name + '-' + this.params.startDate + '_' + this.params.endDate + '.xlsx';

            this.kpis.customer_name = this.params.customer?.Name;
            this.kpis.filename = filename;
            this.kpis.report_alias = 'CUSTOMER-KPI-REPORT';

            console.log('kpis: ', this.kpis);
            axios
                .post('v2/kpis/customers/excel', this.kpis,
                    {
                        responseType: 'blob',
                        headers: {
                            Authorization: 'Bearer ' + this.$store.state.token
                        }
                    }
                )
                .then(response => {
                    let data = response.data;
                    let url = window.URL.createObjectURL(new Blob([data]));
                    let link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();
                    str.state.isLoading = false;
                })
                .catch(error => {
                    console.log(error);
                    str.state.isLoading = false;
                });
        },
        getReport() {
            console.log('Get report');

            // Handle errors
            if (this.params.startDate == '' || this.params.endDate == '') {
                this.$store.dispatch('setSnackbar', {
                    color: 'error',
                    text: 'Please select a date range'
                });
                return;
            } else if (!this.params.customer) {
                this.$store.dispatch('setSnackbar', {
                    color: 'error',
                    text: 'Please select a customer'
                });
                return;
            }

            console.log('Params ', this.params);
            this.$store.state.isLoading = true;
            axios
                .post('v2/kpis/customers/report', this.params)
                .then(response => {
                    let data = response.data;

                    let kpis_by_region_with_region_name = [];
                    for (let region in data.kpis_by_region) {
                        kpis_by_region_with_region_name.push({
                            region_name: region,
                            ...data.kpis_by_region[region]
                        });
                    }

                    this.kpis = data;
                    this.kpis.kpis_by_region_with_region_name = kpis_by_region_with_region_name;

                    this.$store.state.isLoading = false;
                    this.loading.kpis = true;
                    
                    console.log('kpis: ', this.kpis);
                })
                .catch(error => {
                    console.log(error);
                    this.$store.state.isLoading = false;
                });
        },
        getDateFromDatetime(datetime) {
            if (datetime == null) return '';
            const date = new Date(datetime);

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
            const day = String(date.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        },
        getFormattedCurrencyField(value, decimals = 2) {
            let cad = new Intl.NumberFormat('en-CA', {
                style: 'currency',
                currency: 'CAD',
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            });
            return cad.format(value);
        },
        getFormattedNumberField(value, decimalPlaces = 2) {
            if (value == null) return null;
            const number = parseFloat(value);
            return number.toLocaleString('en-US', {
                minimumFractionDigits: decimalPlaces,
                maximumFractionDigits: decimalPlaces,
            });
        },
        getTotalByField(source, field, format = '', decimals = 2) {
            let total = source.reduce((acc, curr) => {
                let value = parseFloat(curr[field]);
                return acc + (!isNaN(value) ? value : 0);
            }, 0);
            total = total.toFixed(decimals);

            if (format == 'currency') total = this.getFormattedCurrencyField(total, decimals);
            else if (format == 'number') total = this.getFormattedNumberField(total, decimals);

            return total;
        },
        handleChangeParams() {
            if (this.loading.kpis) {
                this.loading.kpis = false;
                this.kpis = {};
            }
        }
    },
    watch: {
        
    },
    // created() {
        
    // },
};
</script>


<style>
.custom-hd {
    background-color: #00796B!important;
    color: #fff!important;
}
/* table {
    border-collapse: collapse;
    width: 100%;
    table-layout: fixed;
}
table th, td {
    border: 1px solid #dddddd;
    padding: 8px;
    text-align: left;
} */
</style>